import { Loader2, Send, Square } from "lucide-react";
import * as React from "react";
import { cn } from "src/common/utils";
import { Textarea } from "src/components/ui/textarea";

interface ChatInputProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement>{
  onSend?: (message: string) => void;
  sendDisabled?: boolean;
  defaultValue?: string;
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
  // Keep value for backwards compatibility but we won't use it as a controlled prop
  value?: string;
  // Interrupt button props
  showInterrupt?: boolean;
  onInterrupt?: () => void;
  isInterrupting?: boolean;
  // Callback when user tries to submit while sending is disabled
  onSubmitWhileDisabled?: (message: string) => void;
  // Left-edge inset (px) for the textarea content + custom placeholder span.
  // Use when an adornment (e.g. attach button) sits at the inside-left of the input.
  leftInsetPx?: number;
}

interface ChatInputRef extends HTMLTextAreaElement {
  setInputMessage?: (message: string) => void;
}

const MAX_TEXTAREA_HEIGHT = 300;

const ChatInput = React.forwardRef<ChatInputRef, ChatInputProps>(
  ({ className, onSend, sendDisabled, defaultValue, onChange, value: _valueIgnored, showInterrupt, onInterrupt, isInterrupting, onSubmitWhileDisabled, leftInsetPx, ...props }, ref) => {
    const { style: incomingStyle, disabled, placeholder, ...restProps } = props;
    const placeholderText = typeof placeholder === "string" ? placeholder : "";

    // Add ref for height management
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);
    const pendingAnimationFrameRef = React.useRef<number | null>(null);
    const lastMeasuredHeightRef = React.useRef<number | null>(null);
    const [isOverflowing, setIsOverflowing] = React.useState(false);
    const isOverflowingRef = React.useRef(false);
    const [message, setMessage] = React.useState(defaultValue || _valueIgnored || "");

    // Defer measurement so typing does not trigger synchronous reflow on every key
    const scheduleHeightAdjustment = React.useCallback(() => {
      if (typeof window === "undefined") {
        return;
      }

      if (pendingAnimationFrameRef.current !== null) {
        return;
      }

      pendingAnimationFrameRef.current = window.requestAnimationFrame(() => {
        pendingAnimationFrameRef.current = null;
        const textarea = textareaRef.current;
        if (!textarea) {
          return;
        }

        textarea.style.height = "auto";
        const nextHeight = textarea.scrollHeight;
        const targetHeight = Math.min(nextHeight, MAX_TEXTAREA_HEIGHT);
        const shouldOverflow = nextHeight > MAX_TEXTAREA_HEIGHT;

        if (lastMeasuredHeightRef.current !== targetHeight) {
          textarea.style.height = `${targetHeight}px`;
          lastMeasuredHeightRef.current = targetHeight;
        } else {
          textarea.style.height = `${targetHeight}px`;
        }

        textarea.style.overflowY = shouldOverflow ? "auto" : "hidden";

        if (isOverflowingRef.current !== shouldOverflow) {
          isOverflowingRef.current = shouldOverflow;
          setIsOverflowing(shouldOverflow);
        }
      });
    }, []);

    React.useEffect(() => {
      return () => {
        if (pendingAnimationFrameRef.current !== null && typeof window !== "undefined") {
          window.cancelAnimationFrame(pendingAnimationFrameRef.current);
          pendingAnimationFrameRef.current = null;
        }
      };
    }, []);

    // Method to set message from outside
    const setInputMessage = React.useCallback((newMessage: string) => {
      setMessage(newMessage);
      scheduleHeightAdjustment();
    }, [scheduleHeightAdjustment]);

    // Combine refs and expose the setInputMessage method
    const combinedRef = React.useCallback((node: HTMLTextAreaElement | null) => {
      if (!node) return;

      // @ts-ignore - add our custom method to the DOM node
      node.setInputMessage = setInputMessage;

      // @ts-ignore
      textareaRef.current = node;
      if (typeof ref === "function") ref(node as ChatInputRef);
      else if (ref) ref.current = node as ChatInputRef;

      scheduleHeightAdjustment();
    }, [ref, scheduleHeightAdjustment, setInputMessage]);

    React.useEffect(() => {
      scheduleHeightAdjustment();
    }, [scheduleHeightAdjustment]);
    
    const isSendingDisabled = sendDisabled || disabled;
    const isPlaceholderVisible = message.length === 0;
    
    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Always handle Enter key (with or without shift)
      if (e.key === "Enter") {
        // If shift is pressed, allow normal behavior (new line)
        if (e.shiftKey) {
          return; // Let default behavior happen (new line)
        }

        // Always prevent default for plain Enter (no shift)
        e.preventDefault();

        // If there's a message but sending is disabled, notify parent (don't clear input yet)
        if (isSendingDisabled && message.trim() && onSubmitWhileDisabled) {
          onSubmitWhileDisabled(message.trim());
          return;
        }

        // Only send message if sending is not disabled
        if (!isSendingDisabled && message.trim() && onSend) {
          onSend(message.trim());
          setMessage("");

          // Reset height after sending
          if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
            lastMeasuredHeightRef.current = null;
            scheduleHeightAdjustment();
          }
        }
      }
    };

    return (
      <div className="w-full relative">
        {/* Input area with right padding to leave room for button */}
        <Textarea
          autoComplete="off"
          ref={combinedRef}
          name="message"
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
            scheduleHeightAdjustment();

            // Call the provided onChange handler if provided
            onChange?.(e);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholderText}
          className={cn(
            "max-h-[300px] px-4 py-2 bg-background text-sm placeholder:text-transparent disabled:cursor-not-allowed disabled:opacity-50 w-full rounded-md resize-none",
            disabled ? "opacity-70" : "",
            className,
          )}
          style={{
            overflowX: "hidden",
            overflowY: isOverflowing ? "auto" : "hidden",
            overflowWrap: isPlaceholderVisible ? "normal" : "anywhere",
            whiteSpace: isPlaceholderVisible ? "nowrap" : "pre-wrap",
            textOverflow: isPlaceholderVisible ? "ellipsis" : undefined,
            paddingRight: "72px",
            ...(leftInsetPx ? { paddingLeft: `${leftInsetPx}px` } : {}),
            ...incomingStyle,
          }}
          disabled={disabled}
          {...restProps}
        />

        {isPlaceholderVisible && placeholderText ? (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-0 flex items-center px-4 text-sm text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis"
            style={{ right: "44px", ...(leftInsetPx ? { paddingLeft: `${leftInsetPx}px` } : {}) }}
          >
            {placeholderText}
          </span>
        ) : null}

        {/* Button area - send or interrupt button */}
        <div className="absolute right-0 top-0 bottom-0 flex items-center justify-center">
          <div className="w-12 flex items-center justify-center">
            {showInterrupt ? (
              /* Interrupt button */
              <button
                className="h-8 w-8 flex items-center justify-center rounded-full transition-colors duration-200 text-red-400 hover:bg-red-500/10 hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={onInterrupt}
                disabled={isInterrupting}
                title={isInterrupting ? "Stopping..." : "Stop thinking"}
              >
                {isInterrupting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Square className="w-5 h-5 fill-current" />
                )}
              </button>
            ) : (
              /* Send button */
              <button
                className={cn(
                  "h-8 w-8 flex items-center justify-center rounded-full transition-colors duration-200",
                  message.trim() && !isSendingDisabled
                    ? "text-foreground hover:bg-foreground/10"
                    : "text-muted-foreground/50 cursor-default"
                )}
                onClick={() => {
                  if (message.trim() && onSend && !isSendingDisabled) {
                    onSend(message.trim());
                    setMessage("");

                    // Reset height after sending
                    if (textareaRef.current) {
                      textareaRef.current.style.height = "auto";
                      lastMeasuredHeightRef.current = null;
                      scheduleHeightAdjustment();
                    }
                  }
                }}
                disabled={!message.trim() || isSendingDisabled}
              >
                <Send className={cn(
                  "w-5 h-5",
                  isSendingDisabled ? "opacity-50" : ""
                )} />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  },
);
ChatInput.displayName = "ChatInput";

export { ChatInput };
